import { useForm } from "@mantine/form";
import type { UseFormInput, UseFormReturnType } from "@mantine/form";
import { handleFormErrors, scrollToFirstError } from "../utils/formErrorHandler";
import { toIsoDateTime, toDateOnly } from "../utils/mantineDate";

interface UseAppFormOptions<Values> {
  /**
   * Fallback toast copy for onSubmitSafe when the server gives no error
   * body (network failure, bare 5xx). Passed to handleFormErrors.
   */
  errorFallback?: string;
  /**
   * Fields whose Mantine date/datetime picker value must be submitted as a
   * strict ISO-8601 datetime string (backend `z.string().datetime()`).
   *
   * Mantine v9 pickers emit local "YYYY-MM-DD HH:mm:ss" strings — NOT ISO —
   * which `z.string().datetime()` rejects ("Invalid ISO"); and `new Date(...)`
   * on that space-separated format throws on Safari/iPad. Declaring the field
   * here routes it through the single dayjs-based `toIsoDateTime` converter at
   * submit (Mantine `transformValues`), so the conversion lives in ONE place
   * instead of being hand-rolled — and forgotten — per form (SCRUM-125).
   */
  dateTimeFields?: (keyof Values)[];
  /**
   * Fields submitted as a date-only "YYYY-MM-DD" string (backend
   * `z.string().date()` / a `@db.Date` column). Converted via `toDateOnly`,
   * which formats the LOCAL calendar day (no UTC day-shift) and is Safari-safe.
   */
  dateOnlyFields?: (keyof Values)[];
}

type OnSubmitHandler<Values, TransformedValues> = Parameters<
  UseFormReturnType<Values, TransformedValues>["onSubmit"]
>[0];

export type AppFormReturnType<
  Values extends Record<string, unknown>,
  TransformedValues = Values,
> = UseFormReturnType<Values, TransformedValues> & {
  /**
   * form.onSubmit + the US-080/081 error contract in one line: a submit
   * blocked by validation scrolls to the first errored field, and a thrown
   * submission lands in handleFormErrors (400 details → inline field
   * errors; anything else → one generic toast). Pair the mutation with
   * FORM_MUTATION_META so the global handler stays silent.
   */
  onSubmitSafe: (
    handleSubmit: OnSubmitHandler<Values, TransformedValues>,
  ) => (event?: React.SyntheticEvent<HTMLFormElement>) => void;
};

/**
 * useForm with the app's submission UX built in (US-081):
 *
 * - `form.onSubmit(handler)` defaults the invalid-submit handler to
 *   scrollToFirstError; an explicitly passed second argument still wins.
 * - `form.onSubmitSafe(handler)` additionally catches a thrown/rejected
 *   handler with handleFormErrors.
 *
 * Coexists with plain useForm — adopt per form, no big-bang migration.
 *
 * The returned object is MUTATED, not spread: Mantine passes the form by
 * reference into useFormActions and enhanceGetInputProps, so a spread copy
 * would leave named-form actions holding the unwrapped onSubmit. Mantine
 * rebuilds the form object every render and nothing inside it calls
 * form.onSubmit (verified against @mantine/form 9.2.1), so per-render
 * reassignment is safe and never stacks wrappers.
 */
export function useAppForm<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Values extends Record<string, any>,
  TransformedValues = Values,
>(
  input?: UseFormInput<Values, TransformedValues>,
  options: UseAppFormOptions<Values> = {},
): AppFormReturnType<Values, TransformedValues> {
  const { dateTimeFields, dateOnlyFields } = options;

  // Single, Safari-safe date-normalization step applied at submit time via
  // Mantine `transformValues`. It only transforms the SUBMITTED payload —
  // `form.values` (and therefore the picker display) keep the raw local value.
  // Composes with a form's own `transformValues` (date normalization runs
  // first). No-op when no date fields are declared, so existing callers are
  // unaffected.
  const normalizeDates = (values: Values): Values => {
    if (!dateTimeFields?.length && !dateOnlyFields?.length) return values;
    const next: Record<string, unknown> = { ...values };
    for (const field of dateTimeFields ?? [])
      next[field as string] = toIsoDateTime(
        next[field as string] as string | Date | null | undefined,
      );
    for (const field of dateOnlyFields ?? [])
      next[field as string] = toDateOnly(
        next[field as string] as string | Date | null | undefined,
      );
    return next as Values;
  };

  const userTransform = input?.transformValues;
  const mergedInput = input
    ? ({
        ...input,
        transformValues: userTransform
          ? (values: Values) => userTransform(normalizeDates(values))
          : normalizeDates,
      } as UseFormInput<Values, TransformedValues>)
    : input;

  const form = useForm<Values, TransformedValues>(
    mergedInput,
  ) as AppFormReturnType<Values, TransformedValues>;

  const baseOnSubmit = form.onSubmit;
  // Mutation is deliberate (see the hook docstring): Mantine holds this
  // object by reference in useFormActions/enhanceGetInputProps, so a spread
  // copy would desync them. Mantine rebuilds the object every render —
  // nothing is shared across renders, so this is safe to mutate.
  // eslint-disable-next-line react-hooks/immutability
  form.onSubmit = (handleSubmit, handleValidationFailure) =>
    baseOnSubmit(handleSubmit, handleValidationFailure ?? scrollToFirstError);

  // eslint-disable-next-line react-hooks/immutability
  form.onSubmitSafe = (handleSubmit) =>
    form.onSubmit(async (values, event) => {
      try {
        await handleSubmit(values, event);
      } catch (error) {
        handleFormErrors(error, form, options.errorFallback);
      }
    });

  return form;
}
